import _defineProperty from "@babel/runtime/helpers/defineProperty";
/*
 *                     NOTICE
 *            COPYRIGHT 2018 Ab Initio
 *       UNPUBLISHED -- ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import { withEditManager } from "abreact/incubator/ui/managed_editing/EditManager";
import * as React from "react";

/** This class sits in between an editable item and an edit manager.
 * Implements common logic for editable items.
 * The editable item talks to the IEditableItemModel interface
 * The edit manager talks to the IEditableItem interface
 * Typically instantiated by the withEditableItemModel HOC wrapper
 */
export class EditableItemModel {
  constructor(editManager) {
    _defineProperty(this, "editManager", void 0);
    _defineProperty(this, "editableItem", void 0);
    _defineProperty(this, "isMounted", false);
    // set by owner
    _defineProperty(this, "canBeEdited", false);
    _defineProperty(this, "registeredWithManager", false);
    _defineProperty(this, "currentlyEditing", false);
    _defineProperty(this, "managerIsInEditMode", false);
    _defineProperty(this, "onStateChange", void 0);
    this.editManager = editManager;
  }

  /**
   * True if the editable item exists within a context (such as an edit panel) that is in edit mode.
   *
   * This is true whether or not the item itself can be edited.
   */
  get editableItemContextIsInEditMode() {
    return this.managerIsInEditMode;
  }

  /**
   * Used by owner of model.
   * the onStateChange callback will be called whenever this changes.
   * @returns  true if editable item should be in edit mode, false for display mode.
   */
  get isInEditMode() {
    return this.currentlyEditing && this.canBeEdited;
  }
  get isManaged() {
    return this.editManager != null;
  }
  managerEditModeChanged(inEditMode) {
    this.managerIsInEditMode = inEditMode;
    this.callOnStateChange();
  }
  validationIssues() {
    return this.editableItem && this.editableItem.validationIssues ? this.editableItem.validationIssues() : [];
  }
  startEditing() {
    this.currentlyEditing = true;
    this.editableItem && this.editableItem.startEditing && this.editableItem.startEditing();
    this.callOnStateChange();
  }
  endEditing() {
    this.currentlyEditing = false;
    this.editableItem && this.editableItem.endEditing && this.editableItem.endEditing();
    this.callOnStateChange();
  }
  callOnStateChange() {
    this.onStateChange && this.onStateChange();
  }
  saveEditing() {
    if (this.editableItem && this.editableItem.saveEditing) {
      return this.editableItem.saveEditing();
    } else {
      return true;
    }
  }
  revertEditing() {
    if (this.editableItem && this.editableItem.revertEditing) {
      return this.editableItem.revertEditing();
    }
  }

  // Called from editable item
  setItemWasChanged(wasChanged) {
    this.editManager && this.editManager.setItemWasChanged(this, wasChanged);
  }
  setDynamicValidationIssues(issues) {
    this.editManager && this.editManager.setDynamicValidationIssues(this, issues);
  }

  /**
   * Called by component implementation to explicitly set the callbacks,
   * as opposed to implicit setting via withEditableItemModel finding the component.
   */
  setCallbacks(item) {
    this.editableItem = item;
  }

  /**
   * Called by withEditableItemModel component to set the automatic callbacks based on the child component.
   * These will be overidden by any calls to setCallbacks(), and only take effect if setCallbacks() has not been called.
   */
  setAutomaticCallbacks(item) {
    if (this.editableItem == null) {
      this.editableItem = item;
    }
  }
  setEditable(editable) {
    if (this.canBeEdited !== editable) {
      this.canBeEdited = editable;
      this.updateRegistration();
    }
  }

  /** For use by wrapper component that owns this model.
   * Call on componentDidMount / componentWillUnmount
   */
  setIsMounted(isMounted) {
    this.isMounted = isMounted;
    this.updateRegistration();
  }
  updateRegistration() {
    if (this.editManager) {
      // Only register items once they have been mounted by React. Currently,
      // we register even non-editable items so that they can react to changes
      // in edit mode.
      let shouldBeRegistered = this.isMounted;
      if (shouldBeRegistered) {
        if (!this.registeredWithManager) {
          this.registeredWithManager = true;
          this.editManager.register(this, this.canBeEdited);
        } else {
          // If we were already registered, then just update the "can be edited" state
          this.editManager.setItemCanBeEdited(this, this.canBeEdited);
        }
      }
      if (!shouldBeRegistered && this.registeredWithManager) {
        this.registeredWithManager = false;
        this.editManager.deregister(this);
      }
    }
  }
}

/** These are the properties injected to a component's props when wrapped using withEditableItemModel */

export function withEditableItemModel(base) {
  /* This class:
    1. Owns the editable model which implements most of the editable item logic
    2. Picks up the react ref to the editable item component and gives to model to let it call action callbacks
         on the editable item directly.
    3. Handles react lifecycle methods to let model know when component is mounted / unmounted
   */
  let component = class WithEditableItemModel extends React.Component {
    constructor(props) {
      super(props);
      _defineProperty(this, "model", void 0);
      _defineProperty(this, "setBaseRef", ref => {
        this.model.setAutomaticCallbacks(ref);
      });
      this.model = new EditableItemModel(props.editManager);
      this.model.onStateChange = this.forceUpdate.bind(this);
    }
    render() {
      // use local for type safety
      let context = {
        editableItemModel: this.model,
        editableItemIsInEditMode: this.model.isInEditMode,
        editableItemContextIsInEditMode: this.model.editableItemContextIsInEditMode
      };
      return /*#__PURE__*/React.createElement(base, {
        ...this.props,
        ...context,
        ref: this.setBaseRef
      });
    }
    componentDidMount() {
      this.model.setIsMounted(true);
    }
    componentWillUnmount() {
      this.model.setIsMounted(false);
    }
  };
  return withEditManager(component);
}
//# sourceMappingURL=WithEditableItemModel.js.map
